import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { 
  ArrowLeft, 
  Calendar, 
  X, 
  Check, 
  Clock, 
  Ban,
  Pause,
  Play,
  Edit,
  FileText
} from 'lucide-react';
import useApi from '../../../hooks/useApi';
import Message from '../../../components/Message';
import Pagination from '../../../components/Pagination';
import { 
  getSentInvitations, 
  cancelInvitation, 
  blockInvitation, 
  pauseInvitation, 
  resumeInvitation,
  updateInvitationPermission
} from '../../../services/invitationsService';

const SentInvitationsPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);
  const statusFilter = queryParams.get('status') || 'all';
  
  const [invitations, setInvitations] = useState([]);
  const [statusCounts, setStatusCounts] = useState({
    all: 0,
    pending: 0,
    accepted: 0,
    rejected: 0,
    cancel: 0,
    blocked: 0,
    paused: 0
  });
  const [pagination, setPagination] = useState({
    page: 1,
    page_size: 10,
    total_pages: 0,
    count: 0
  });

  const { 
    loading, 
    error, 
    callApi: fetchInvitations 
  } = useApi(getSentInvitations, {showSuccessToast: false});
  
  const { 
    loading: cancelLoading, 
    error: cancelError, 
    callApi: cancelInvitationApi,
    successMessage: cancelSuccess,
    setSuccessMessage: setCancelSuccess
  } = useApi(cancelInvitation, {showSuccessToast: true, msg: 'Invitation cancelled successfully'});

  const { 
    loading: blockLoading, 
    error: blockError, 
    callApi: blockInvitationApi,
    successMessage: blockSuccess,
    setSuccessMessage: setBlockSuccess
  } = useApi(blockInvitation, {showSuccessToast: true, msg: 'Invitation blocked successfully'});

  const { 
    loading: pauseLoading, 
    error: pauseError, 
    callApi: pauseInvitationApi,
    successMessage: pauseSuccess,
    setSuccessMessage: setPauseSuccess
  } = useApi(pauseInvitation, {showSuccessToast: true, msg: 'Invitation paused successfully'});

  const { 
    loading: resumeLoading, 
    error: resumeError, 
    callApi: resumeInvitationApi,
    successMessage: resumeSuccess,
    setSuccessMessage: setResumeSuccess
  } = useApi(resumeInvitation, {showSuccessToast: true, msg: 'Invitation resumed successfully'});

  const { 
    loading: updatePermissionLoading, 
    error: updatePermissionError, 
    callApi: updatePermissionApi,
    successMessage: updatePermissionSuccess,
    setSuccessMessage: setUpdatePermissionSuccess
  } = useApi(updateInvitationPermission, {showSuccessToast: true, msg: 'Permission updated successfully'});

  const actionLoading = cancelLoading || blockLoading || pauseLoading || resumeLoading || updatePermissionLoading;
  const actionError = cancelError || blockError || pauseError || resumeError || updatePermissionError;
  const successMessage = cancelSuccess || blockSuccess || pauseSuccess || resumeSuccess || updatePermissionSuccess;

  const loadInvitations = useCallback(async () => {
    const data = await fetchInvitations(pagination.page, pagination.page_size, statusFilter);
    if (data) {
      setInvitations(data.results);
      setStatusCounts(data.status_counts || {});
      setPagination(prev => ({
        ...prev,
        total_pages: data.total_pages,
        count: data.count
      }));
    }
  }, [fetchInvitations, statusFilter, pagination.page, pagination.page_size]);

  useEffect(() => {
    loadInvitations();
  }, [loadInvitations, statusFilter]);

  const handlePageChange = (newPage) => {
    setPagination((prev) => ({ ...prev, page: newPage }));
  };

  const handlePageSizeChange = (newPageSize) => {
    setPagination((prev) => ({ ...prev, page_size: newPageSize, page: 1 }));
  };

  const handleCancelInvitation = async (invitationId) => {
    try {
      await cancelInvitationApi(invitationId);
      loadInvitations();
    } catch (err) {
      console.error('Error cancelling invitation:', err);
    }
  };

  const handleBlockInvitation = async (invitationId) => {
    try {
      await blockInvitationApi(invitationId);
      loadInvitations();
    } catch (err) {
      console.error('Error blocking invitation:', err);
    }
  };

  const handlePauseInvitation = async (invitationId) => {
    try {
      await pauseInvitationApi(invitationId);
      loadInvitations();
    } catch (err) {
      console.error('Error pausing invitation:', err);
    }
  };

  const handleResumeInvitation = async (invitationId) => {
    try {
      await resumeInvitationApi(invitationId);
      loadInvitations();
    } catch (err) {
      console.error('Error resuming invitation:', err);
    }
  };

  const handleUpdatePermission = async (invitationId, newPermissionLevel) => {
    try {
      await updatePermissionApi(invitationId, newPermissionLevel);
      loadInvitations();
    } catch (err) {
      console.error('Error updating permission:', err);
    }
  };



  const getStatusBadge = (status) => {
    switch(status) {
      case 'accepted':
        return <span className="bg-green-100 text-green-800 px-2 py-1 rounded-full text-xs font-medium flex items-center"><Check className="w-3 h-3 mr-1" />Accepted</span>;
      case 'rejected':
        return <span className="bg-red-100 text-red-800 px-2 py-1 rounded-full text-xs font-medium flex items-center"><X className="w-3 h-3 mr-1" />Rejected</span>;
      case 'cancelled':
        return <span className="bg-gray-100 text-gray-800 px-2 py-1 rounded-full text-xs font-medium flex items-center"><X className="w-3 h-3 mr-1" />Cancelled</span>;
      case 'blocked':
        return <span className="bg-red-100 text-red-800 px-2 py-1 rounded-full text-xs font-medium flex items-center"><Ban className="w-3 h-3 mr-1" />Blocked</span>;
      case 'paused':
        return <span className="bg-yellow-100 text-yellow-800 px-2 py-1 rounded-full text-xs font-medium flex items-center"><Pause className="w-3 h-3 mr-1" />Paused</span>;
      default:
        return <span className="bg-blue-100 text-blue-800 px-2 py-1 rounded-full text-xs font-medium flex items-center"><Clock className="w-3 h-3 mr-1" />Pending</span>;
    }
  };

  const getPageTitle = () => {
    switch(statusFilter) {
      case 'pending': return 'Pending Sent Invitations';
      case 'accepted': return 'Accepted Sent Invitations';
      case 'rejected': return 'Rejected Sent Invitations';
      case 'cancelled': return 'Cancelled Sent Invitations';
      case 'blocked': return 'Blocked Sent Invitations';
      case 'paused': return 'Paused Sent Invitations';
      default: return 'All Sent Invitations';
    }
  };

  const statusButtons = [
    { key: 'all', label: 'All', count: statusCounts.all },
    { key: 'pending', label: 'Pending', count: statusCounts.pending },
    { key: 'accepted', label: 'Accepted', count: statusCounts.accepted },
    { key: 'rejected', label: 'Rejected', count: statusCounts.rejected },
    { key: 'cancelled', label: 'Cancelled', count: statusCounts.cancel },
    { key: 'blocked', label: 'Blocked', count: statusCounts.blocked },
    { key: 'paused', label: 'Paused', count: statusCounts.paused },
  ];

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-2">
          <button 
            onClick={() => navigate('/dashboard/invitations')}
            className="p-2 rounded-full hover:bg-gray-100"
          >
            <ArrowLeft className="w-5 h-5" />
          </button>
          <h1 className="text-2xl font-bold text-gray-900">{getPageTitle()}</h1>
        </div>
      </div>

      {/* Status Filter Buttons */}
      <div className="flex flex-wrap gap-2">
        {statusButtons.map((button) => (
          <button
            key={button.key}
            onClick={() => navigate(`/dashboard/invitations/sent?status=${button.key}`)}
            className={`px-4 py-2 text-sm font-medium rounded-lg border ${
              statusFilter === button.key
                ? 'bg-primary-600 text-white border-primary-600'
                : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'
            }`}
          >
            {button.label} ({button.count || 0})
          </button>
        ))}
      </div>

      {/* Success/Error Messages */}
      {successMessage && (
        <Message type="success" message={successMessage} />
      )}
      {error && (
        <Message type="error" message={error} />
      )}
      {actionError && (
        <Message type="error" message={actionError} />
      )}

      {/* Invitations List */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
        {loading ? (
          <div className="p-4 text-center">Loading invitations...</div>
        ) : invitations.length === 0 ? (
          <div className="p-6 text-center">
            <p className="text-gray-500">No {statusFilter !== 'all' ? statusFilter : ''} sent invitations found.</p>
            <button
              onClick={() => navigate('/dashboard/collaborators')}
              className="mt-4 inline-flex items-center px-4 py-2 bg-primary-600 rounded-lg text-sm font-medium text-white hover:bg-primary-700"
            >
              Back to Collaborators
            </button>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Recipient</th>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Permission</th>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Sent Date</th>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {invitations.map(invitation => (
                  <tr key={invitation.id}>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center">
                        <div className="h-8 w-8 rounded-full bg-gray-200 flex items-center justify-center text-gray-600 font-medium">
                          {invitation.recipient_details?.username?.charAt(0).toUpperCase() || 'U'}
                        </div>
                        <div className="ml-3">
                          <p className="text-sm font-medium text-gray-900">{invitation.recipient_details?.username || 'User'}</p>
                          <p className="text-xs text-gray-500">{invitation.recipient_details?.email || ''}</p>
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      {(invitation.status === 'pending' || invitation.status === 'accepted') ? (
                        <select
                          value={invitation.permission_level}
                          onChange={(e) => handleUpdatePermission(invitation.id, e.target.value)}
                          disabled={actionLoading}
                          className="text-sm text-gray-900 border border-gray-300 rounded-md px-2 py-1 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500 disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                          <option value="view">View</option>
                          <option value="view_download">View & Download</option>
                          <option value="full_management">Full Management</option>
                        </select>
                      ) : (
                        <span className="text-sm text-gray-900">
                          {invitation.permission_level === 'view' && 'View'}
                          {invitation.permission_level === 'view_download' && 'View & Download'}
                          {invitation.permission_level === 'full_management' && 'Full Management'}
                        </span>
                      )}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      {getStatusBadge(invitation.status)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center text-sm text-gray-500">
                        <Calendar className="w-4 h-4 mr-1" />
                        {new Date(invitation.sent_at).toLocaleDateString()}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex space-x-2">
                        {invitation.status === 'pending' && (
                          <button
                            onClick={() => handleCancelInvitation(invitation.id)}
                            disabled={actionLoading}
                            className="inline-flex items-center px-3 py-1 bg-gray-600 rounded-lg text-xs font-medium text-white hover:bg-gray-700"
                          >
                            <X className="w-3 h-3 mr-1" />
                            Cancel
                          </button>
                        )}
                        {invitation.status === 'accepted' && (
                          <>
                            {/* <button
                              onClick={() => navigate(`/dashboard/collaborator/${invitation.id}/expenses`)}
                              className="inline-flex items-center px-3 py-1 bg-blue-600 rounded-lg text-xs font-medium text-white hover:bg-blue-700"
                            >
                              <FileText className="w-3 h-3 mr-1" />
                              View Reports
                            </button> */}
                            <button
                              onClick={() => handlePauseInvitation(invitation.id)}
                              disabled={actionLoading}
                              className="inline-flex items-center px-3 py-1 bg-yellow-600 rounded-lg text-xs font-medium text-white hover:bg-yellow-700"
                            >
                              <Pause className="w-3 h-3 mr-1" />
                              Pause
                            </button>
                            <button
                              onClick={() => handleBlockInvitation(invitation.id)}
                              disabled={actionLoading}
                              className="inline-flex items-center px-3 py-1 bg-red-600 rounded-lg text-xs font-medium text-white hover:bg-red-700"
                            >
                              <Ban className="w-3 h-3 mr-1" />
                              Block
                            </button>
                          </>
                        )}
                        {invitation.status === 'paused' && (
                          <button
                            onClick={() => handleResumeInvitation(invitation.id)}
                            disabled={actionLoading}
                            className="inline-flex items-center px-3 py-1 bg-green-600 rounded-lg text-xs font-medium text-white hover:bg-green-700"
                          >
                            <Play className="w-3 h-3 mr-1" />
                            Resume
                          </button>
                        )}
                        {(invitation.status === 'rejected' || invitation.status === 'cancelled' || invitation.status === 'blocked') && (
                          <span className="text-gray-400 text-xs">No actions available</span>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Pagination */}
      <Pagination
        currentPage={pagination.page}
        totalPages={pagination.total_pages}
        totalItems={pagination.count}
        pageSize={pagination.page_size}
        onPageChange={handlePageChange}
        onPageSizeChange={handlePageSizeChange}
        loading={loading}
        itemName="Invitations"
      />
    </div>
  );
};

export default SentInvitationsPage;